<template
  ><section class="modern-region">
    <region-header
      ref="navigationHeader"
      :regions="quickNavigationRegionList"
      :categories="visibleNavigationCategoryList"
      :selected-region-id="selectedNavigationRegionId"
      :selected-category-code="selectedCategoryCode"
      :search-mode="searchMode"
      :keyword="searchInputKeyword"
      :disabled="isInterfaceManagement"
      :sort-content-loading="navigationBatchLoading || navigationSectionRefreshLoading"
      @region="navigationRegionChange"
      @category="navigationCategoryChange"
      @open-search="openNavigationSearch"
      @input="navigationSearchInput"
      @search="submitNavigationSearch"
      @clear="clearNavigationSearch"
      @close-search="closeNavigationSearch"
    />
    <div ref="content" class="region-content" @scroll.passive="onScroll">
      <template v-if="isInterfaceManagement"
        ><h3 class="sort-section-title">场景排序</h3>
        <draggable
          :animation="200" :force-fallback="true"
          v-model="ServeData.sceneList"
          class="control-grid scene-grid"
          @change="syncSortedLists"
          ><scene-item-view
            v-for="scene in ServeData.sceneList"
            :key="scene.sceneId"
            :OneScene="scene"
        /></draggable>
        <h3 class="sort-section-title">设备排序</h3>
        <draggable :animation="200" :force-fallback="true" v-model="ServeData.deviceList" class="control-grid" @change="syncSortedLists"
          ><device-item-view
            v-for="device in ServeData.deviceList"
            :key="computedDeviceKey(device.deviceId, device.productId)"
            :oneDevice="device"/></draggable
      ></template>
      <template v-else
        ><section
          v-for="section in renderNavigationSections"
          :key="section.regionId"
          :data-refresh-region="section.regionId"
          class="region-section"
        >
          <h3 v-if="isAllRegionNavigation || searchResultActive" :title="section.regionName">
            {{ section.regionName }}
          </h3>
          <div v-if="section.sceneList.length" class="control-grid scene-grid">
            <scene-item-view
              v-for="scene in section.sceneList"
              :key="scene.sceneId"
              :data-refresh-card="'scene:' + scene.sceneId"
              :OneScene="scene"
            />
          </div>
          <div v-if="section.deviceList.length" class="control-grid">
            <device-item-view
              v-for="device in section.deviceList"
              :key="computedDeviceKey(device.deviceId, device.productId)"
              :data-refresh-card="'device:' + device.deviceId + ':' + device.productId"
              :oneDevice="device"
            />
          </div>
          <p v-if="!section.itemCount" class="control-empty">当前区域暂无设备或场景</p>
          <button
            v-if="section.truncated"
            class="control-link"
            @click="navigationRegionChange(section)"
          >
            查看此区域全部内容
          </button>
        </section></template
      >
      <p
        v-if="!isInterfaceManagement && !renderNavigationSections.length && !navigationBatchLoading && !searchLoading"
        class="control-empty"
      >
        当前区域暂无设备或场景
      </p>
      <p v-if="isInterfaceManagement && !ServeData.sceneList.length && !ServeData.deviceList.length && !navigationBatchLoading" class="control-empty">当前区域暂无可排序内容</p>
      <div v-if="!isInterfaceManagement" class="paging-footer">
        <span v-if="searchResultActive && searchLoading">正在加载更多...</span
        ><span v-else-if="navigationBatchLoading">{{ displayNavigationSections.length ? '正在加载更多区域...' : '正在加载区域内容...' }}</span
        ><button
          v-else-if="searchResultActive ? searchHasMore : !navigationAllLoaded"
          class="control-link"
          @click="navigationScrollToLower"
        >
          {{ searchResultActive ? '加载更多' : '加载更多区域' }}</button
        ><span
          v-else-if="
            searchResultActive ? searchPaginationLoadCompleted : navigationPaginationLoadCompleted
          "
          >已加载全部</span
        >
      </div>
    </div>
  </section></template
>
<script>
import { lightRequest, request } from '@/network/request'
import { getServerApiLevelState } from '@/common/compatibility/server-api-level'
import { usersetStorage } from '@/common/browser-storage'
import { jieMianStateJudge, changefollowOrder, changefollowOrderInc } from '@/common/tool'
import { createSocket, dataStateHandle } from '@/common/webSoket'
import router1 from '@/router'
import {
  ensureRegionNavigationScope,
  requestNextRegionNavigationPage,
  refreshRegionNavigationSnapshot
} from '@/common/region-navigation-data'
import { getControlDataInvalidationScopeKey } from '@/common/control-data-invalidation'
import { createVisibleRegionRefresh, mergeRegionSnapshot } from '@/common/visible-region-refresh'
import {
  getRegionNavigationCategoryPreference,
  setRegionNavigationCategoryPreference
} from '@/common/app-preference'
import SceneItemView from '@/components/content/contro-view/scene-view/scene-item-view'
import DeviceItemView from '@/components/content/contro-view/device-view/device-item-view'
import RegionHeader from './region-navigation-header'
import draggable from 'vuedraggable'
const showTipsToast = (vm, ...args) => vm.$showTipsToast(...args)
// keep-alive 切换分支时，旧子组件可能先被激活；仅当前新版页面可处理导航结果。
const canUseModernNavigation = vm => !vm._isDestroyed && !vm._isBeingDestroyed &&
  !vm._inactive && getServerApiLevelState(vm).useModernRegion
const navigationResponseGuard = vm => {
  const scope = getControlDataInvalidationScopeKey(vm)
  const token = vm.$store.state.token
  return () => canUseModernNavigation(vm) &&
    scope === getControlDataInvalidationScopeKey(vm) && token === vm.$store.state.token
}
const getControlViewportWidth = () => Math.min(1200, window.innerWidth)
const getControlLayoutMetrics = width => ({ columnCount: Math.max(2, Math.floor(width / 180)) })
const QUICK_REGION_LIMIT = 30
const REGION_CONTENT_BATCH_SIZE = 3
const MAX_AUTOMATIC_REGION_BATCHES = Math.ceil(QUICK_REGION_LIMIT / REGION_CONTENT_BATCH_SIZE)
const REGION_TITLE_HEIGHT = 48
const REGION_SECTION_GAP = 8
const REGION_EMPTY_ROW_HEIGHT = 38
const REGION_SCENE_ROW_HEIGHT = 74
const REGION_DEVICE_ROW_HEIGHT = 124
const REGION_SCENE_DEVICE_GAP = 4
const REGION_SCROLL_LOAD_THRESHOLD = 120
const normalizedRegionId = value => String(value == null ? '' : value)
const sameRegionId = (left, right) => normalizedRegionId(left) === normalizedRegionId(right)
export default {
  data() {
    return {
      controlViewportWidth: getControlViewportWidth(),
      savedContentScrollTop: 0,
      restoringContentScroll: false,
      navigationScrollLoadThreshold: REGION_SCROLL_LOAD_THRESHOLD,
      ServeData: {
        deviceList: [],
        sceneList: []
      },
      navigationRegionList: [],
      navigationCategoryList: [],
      navigationContentReady: false,
      navigationConfirmedCategoryAvailability: {},
      navigationConfirmedAvailableCategoryCodes: [],
      selectedCategoryCode: getRegionNavigationCategoryPreference(),
      quickNavigationRegionList: [],
      loadedNavigationRegions: [],
      displayNavigationSections: [],
      navigationBatchLoading: false,
      navigationSectionRefreshLoading: false,
      navigationAllLoaded: false,
      navigationSuccessfulBatchCount: 0,
      navigationAutomaticBatchCount: 0,
      navigationPaginationLoadCompleted: false,
      navigationRequestGeneration: 0,
      navigationInitialized: false,
      navigationContentRegionId: null,
      navigationCacheGeneration: -1,
      headerAlignmentSequence: 0,
      headerAlignmentPendingAfterContent: false,
      searchMode: false,
      searchInputKeyword: '',
      searchAppliedKeyword: '',
      searchSceneList: [],
      searchDeviceList: [],
      searchRegionOrder: [],
      searchPageNo: 0,
      searchPageSize: 30,
      searchHasMore: false,
      searchAutoFillBlocked: false,
      searchLoading: false,
      searchPaginationLoadCompleted: false,
      searchRequestGeneration: 0,
      searchKeyboardActive: false,
      sortSubmitting: false
    }
  },

  methods: {
    syncSearchDeviceState(event) {
      if (this.searchResultActive) dataStateHandle(event.detail || [], this.searchDeviceList)
    },
    // A display round starts only at a real visibility transition, never from a data watcher.
    refreshCycle() {
      if (!this._visibleRefresh) this._visibleRefresh = createVisibleRegionRefresh()
      return this._visibleRefresh
    },
    showCachedRegions() {
      if (!canUseModernNavigation(this) || document.hidden) return
      const cycle = this.refreshCycle()
      if (!cycle.show(getControlDataInvalidationScopeKey(this) + ':' + this.$store.state.token)) return
      this._searchRefreshAttempted = false
      this.activatePage(true)
      this.scheduleCachedRegionRefresh()
    },
    hideCachedRegions() {
      this.refreshCycle().hide()
    },
    onDocumentVisibility() {
      if (document.hidden) this.hideCachedRegions()
      else this.showCachedRegions()
    },
    scheduleCachedRegionRefresh() {
      if (this._cachedRefreshScheduled) return
      this._cachedRefreshScheduled = true
      this.$nextTick(() => {
        this._cachedRefreshScheduled = false
        this.flushVisibleRegionRefresh()
      })
    },
    flushVisibleRegionRefresh() {
      if (!canUseModernNavigation(this) || document.hidden || this.restoringContentScroll ||
          this.isInterfaceManagement || this._indexRefreshLoading || this.navigationBatchLoading || this.navigationSectionRefreshLoading) return
      const cycle = this.refreshCycle()
      if (!cycle.isCurrent(cycle.snapshot())) return
      if (this.searchMode) {
        if (!this._searchRefreshAttempted && !this.searchLoading && this.searchAppliedKeyword) {
          this._searchRefreshAttempted = true
          this.refreshNavigationSearchPreservingContent()
        }
        return
      }
      const container = this.$refs.content
      if (!container) return
      const bounds = container.getBoundingClientRect()
      const ids = Array.from(container.querySelectorAll('[data-refresh-region]')).filter(el => {
        const box = el.getBoundingClientRect()
        return box.bottom > bounds.top && box.top < bounds.bottom
      }).map(el => el.getAttribute('data-refresh-region'))
      const loaded = new Set(this.loadedNavigationRegions.map(region => String(region.regionId)))
      const ticket = this.refreshCycle().take(ids.filter(id => loaded.has(id)), REGION_CONTENT_BATCH_SIZE)
      if (ticket) this.refreshLoadedNavigationSections(ticket)
    },
    // Anchor a retained card; also keep the user's newer scroll if they moved during the request.
    captureContentAnchor() {
      const container = this.$refs.content
      if (!container) return () => {}
      const top = container.scrollTop
      const bounds = container.getBoundingClientRect()
      const elements = Array.from(container.querySelectorAll('[data-refresh-card], [data-refresh-region]'))
      const anchor = elements.find(el => el.hasAttribute('data-refresh-card') && el.getBoundingClientRect().bottom > bounds.top) ||
        elements.find(el => el.getBoundingClientRect().bottom > bounds.top)
      const attribute = anchor && anchor.hasAttribute('data-refresh-card') ? 'data-refresh-card' : 'data-refresh-region'
      const id = anchor && anchor.getAttribute(attribute)
      const offset = anchor && anchor.getBoundingClientRect().top - bounds.top
      return () => this.$nextTick(() => {
        if (!canUseModernNavigation(this) || container.scrollTop !== top) return
        const next = Array.from(container.querySelectorAll('[data-refresh-card], [data-refresh-region]'))
          .find(el => el.getAttribute(attribute) === id)
        if (next) container.scrollTop += next.getBoundingClientRect().top - container.getBoundingClientRect().top - offset
        this.savedContentScrollTop = container.scrollTop
      })
    },

    loadNavigationPage() {
      if (!canUseModernNavigation(this)) return
      const isCurrent = navigationResponseGuard(this)
      ensureRegionNavigationScope(this)
      requestNextRegionNavigationPage(this, {
        reset: true
      })
        .then(result => {
          if (!isCurrent() || (result && (result.stale || result.skipped))) return
          this.enableNavigationModeFromCache({
            forceContentRefresh: true
          })
        })
        .catch(error => {
          if (!isCurrent()) return
          console.log('获取新版区域导航数据失败', error)
          showTipsToast(
            this,
            3,
            error && error.message ? error.message : '获取区域导航失败',
            true,
            3000
          )
        })
    },

    refreshNavigationIndexOnActivation() {
      if (!canUseModernNavigation(this)) return
      if (this._indexRefreshLoading) { this._indexRefreshPending = true; return }
      const isCurrent = navigationResponseGuard(this)
      const cycle = this.refreshCycle()
      const ticket = cycle.snapshot()
      const restoreAnchor = this.captureContentAnchor()
      this._indexRefreshLoading = true
      refreshRegionNavigationSnapshot(this, () => isCurrent() && cycle.isCurrent(ticket))
        .then(result => {
          if (!isCurrent() || !cycle.isCurrent(ticket) || !result || result.stale) return
          const cache = this.$store.state.regionnavigation
          this.navigationRegionList = [{ regionId: '', regionName: '全部区域' }].concat(cache.regionList)
          this.navigationCategoryList = ((cache.navigation || {}).categories || []).slice()
          this.navigationCacheGeneration = cache.generation
          // Incomplete navigation pages cannot prove that an unseen region was deleted.
          this.reconcileNavigationContent()
          this.refreshQuickNavigationRegions(this.selectedNavigationRegionId)
          if (!cache.hasMore && this.selectedNavigationRegionId &&
              !cache.regionList.some(region => sameRegionId(region.regionId, this.selectedNavigationRegionId))) {
            this.$store.dispatch('asyUpdataRegionShowPage', { regionId: '', regionName: '全部区域' })
          }
          restoreAnchor()
        })
        .catch(() => { /* Keep the existing navigation and cards on a transient failure. */ })
        .finally(() => {
          this._indexRefreshLoading = false
          if (this._indexRefreshPending) {
            this._indexRefreshPending = false
            if (canUseModernNavigation(this) && !document.hidden) this.refreshNavigationIndexOnActivation()
          }
          this.scheduleCachedRegionRefresh()
          this.scheduleViewportFill()
        })
    },

    enableNavigationModeFromCache(options) {
      if (!canUseModernNavigation(this)) return
      const cache = this.$store.state.regionnavigation

      const navigation = cache.navigation || {}
      if (!(navigation.categories || []).length) {
        // An empty initial/in-flight cache is not a completed invalid response.
        if (!cache.initialized || cache.loading) return
        showTipsToast(this, 3, '区域导航暂时无法加载，请稍后重试', true, 3000)
        return
      }

      // Only consume a generation after its navigation is ready.
      if (this.navigationCacheGeneration !== cache.generation) {
        this.navigationCacheGeneration = cache.generation
        this.navigationConfirmedCategoryAvailability = {}
        this.navigationConfirmedAvailableCategoryCodes = []
        this.quickNavigationRegionList = []
        this.navigationInitialized = false
      }

      this.enableNavigationMode(
        {
          allRegion: cache.regionList,
          followRegion: cache.followRegionList
        },
        navigation,
        options
      )
    },

    enableNavigationMode(regionData, navigation, options) {
      const orderedRegions = (regionData.allRegion || []).slice()
      const allRegion = {
        followState: 0,
        followTime: '',
        regionId: '',
        regionName: '全部区域'
      }
      this.navigationRegionList = [allRegion].concat(orderedRegions)
      this.navigationCategoryList = (navigation.categories || []).slice().sort((left, right) => {
        return Number(left.sortOrder || 0) - Number(right.sortOrder || 0)
      })

      if (
        !this.navigationCategoryList.some(category => category.code === this.selectedCategoryCode)
      ) {
        this.updateSelectedCategory('all')
      }

      const currentId = this.$store.state.publicinfo.regionShowPage.id || ''
      let currentRegion = this.navigationRegionList.find(region =>
        sameRegionId(region.regionId, currentId)
      )

      if (!currentRegion && currentId && this.$store.state.regionnavigation.hasMore) {
        const storedRegion = this.$store.state.publicinfo.regionShowPage
        currentRegion = {
          regionId: currentId,
          regionName: storedRegion.name || storedRegion.regionName || '当前区域'
        }
        this.navigationRegionList.push(currentRegion)
      }

      currentRegion = currentRegion || allRegion
      this.refreshQuickNavigationRegions(currentRegion.regionId)

      if (!sameRegionId(currentRegion.regionId, currentId)) {
        this.$store.dispatch('asyUpdataRegionShowPage', currentRegion)
        usersetStorage('selectRegionInfo', currentRegion)
      }

      if (!options || options.alignHeader !== false) this.requestHeaderAlignment()
      this.prepareInterfaceManagementState()

      if (options && options.forceContentRefresh === true) {
        this.navigationInitialized = true
        this.refreshNavigationSelectionPreservingContent(currentRegion)
        return
      }

      if (
        !this.navigationInitialized ||
        !sameRegionId(this.navigationContentRegionId, currentRegion.regionId)
      ) {
        this.navigationInitialized = true
        this.resetNavigationContent(currentRegion.regionId)
        this.loadNavigationSelection('initial', currentRegion)
        return
      }

      this.reconcileNavigationContent()
      if (this.isAllRegionNavigation) this.updateNavigationAllLoadedState()
      if (this.loadedNavigationRegions.length > 0) this.headerAlignmentPendingAfterContent = false

      if (!this.isAllRegionNavigation && !this.navigationBatchLoading) {
        this.requestNavigationSections(
          [currentRegion],
          this.navigationRequestGeneration,
          false,
          true
        )
      } else if (this.loadedNavigationRegions.length === 0) {
        this.loadNextRegionBatch()
      }
    },

    refreshQuickNavigationRegions(selectedRegionId) {
      const allRegion = this.navigationRegionList[0] || {
        regionId: '',
        regionName: '全部区域'
      }
      const serverRegions = (this.$store.state.regionnavigation.regionList || []).slice()
      const quickRegions = serverRegions.slice(0, QUICK_REGION_LIMIT)
      const selectedInQuickList = quickRegions.some(region =>
        sameRegionId(region.regionId, selectedRegionId)
      )
      const selected = this.navigationRegionList.find(region =>
        sameRegionId(region.regionId, selectedRegionId)
      )
      if (selected && selected.regionId && !selectedInQuickList) quickRegions.push(selected)
      const result = [allRegion].concat(quickRegions)
      const totalNo = Number(this.$store.state.regionnavigation.totalNo || serverRegions.length)

      if (totalNo > QUICK_REGION_LIMIT || this.$store.state.regionnavigation.hasMore) {
        result.push({
          regionId: '__more__',
          regionName: '更多',
          isMore: true
        })
      }

      this.quickNavigationRegionList = result
    },

    reconcileNavigationContent() {
      const regionById = {}
      this.navigationRegionList.slice(1).forEach(region => {
        regionById[normalizedRegionId(region.regionId)] = region
      })
      this.loadedNavigationRegions = this.loadedNavigationRegions
        .filter(region => {
          return regionById[normalizedRegionId(region.regionId)]
        })
        .map(region => ({
          regionId: region.regionId,
          regionName: regionById[normalizedRegionId(region.regionId)].regionName,
          truncated: region.truncated === true
        }))
      this.sortLoadedNavigationRegions()
      const deviceList = this.ServeData.deviceList.filter(
        device => regionById[normalizedRegionId(device.regionId)]
      )
      const sceneList = this.ServeData.sceneList.filter(
        scene => regionById[normalizedRegionId(scene.regionId)]
      )

      if (
        deviceList.length !== this.ServeData.deviceList.length ||
        sceneList.length !== this.ServeData.sceneList.length
      ) {
        this.ServeData = {
          deviceList: deviceList,
          sceneList: sceneList
        }
        this.$store.dispatch('asyUpdataregionServeData', this.ServeData)
      }

      this.updateNavigationAllLoadedState()
      this.refreshDisplayNavigationSections()
    },

    sortLoadedNavigationRegions() {
      const order = {}
      this.navigationRegionList.forEach((region, index) => {
        order[normalizedRegionId(region.regionId)] = index
      })
      this.loadedNavigationRegions.sort((left, right) => {
        return (
          Number(order[normalizedRegionId(left.regionId)] || 0) -
          Number(order[normalizedRegionId(right.regionId)] || 0)
        )
      })
    },

    openNavigationSearch() {
      if (this.isInterfaceManagement) return
      this.searchMode = true
    },

    markNavigationContentReady() {
      if (this.navigationContentReady) return
      this.navigationContentReady = true
    },

    navigationSearchInput(value) {
      this.searchInputKeyword = value || ''
    },

    regionSearchFocusChange(focused) {
      this.searchKeyboardActive = focused === true
    },

    dismissRegionSearchKeyboard() {
      const header = this.$refs.navigationHeader
      if (header && typeof header.dismissSearchKeyboard === 'function')
        header.dismissSearchKeyboard()
      else this.searchKeyboardActive = false
    },

    submitNavigationSearch(options) {
      const keyword = String(this.searchInputKeyword || '').trim()

      if (!keyword) {
        this.clearNavigationSearch()
        return
      }

      const forceRefresh = options && options.force === true
      if (
        !forceRefresh &&
        keyword === this.searchAppliedKeyword &&
        (this.searchLoading || this.searchPageNo > 0)
      )
        return
      this.searchAppliedKeyword = keyword
      this.searchRequestGeneration += 1

      this.searchPageNo = 0
      this.searchHasMore = true
      this.searchLoading = false
      this.searchPaginationLoadCompleted = false
      this.navigationContentReady = false
      this.loadNavigationSearch(true)
    },

    clearNavigationSearch() {
      this.searchInputKeyword = ''
      this.searchAppliedKeyword = ''
      this.searchRequestGeneration += 1
      this.searchSceneList = []
      this.searchDeviceList = []
      this.searchRegionOrder = []
      this.searchPageNo = 0
      this.searchAutoFillBlocked = false
      this.searchHasMore = false
      this.searchLoading = false
      this.searchPaginationLoadCompleted = false
    },

    closeNavigationSearch() {
      this.clearNavigationSearch()
      this.searchMode = false
      this.navigationContentReady =
        this.navigationInitialized &&
        (this.displayNavigationSections.length > 0 || this.navigationAllLoaded)
      const currentRegion = this.navigationRegionList.find(region => {
        return sameRegionId(region.regionId, this.selectedNavigationRegionId)
      })
      if (currentRegion) this.refreshNavigationSelectionPreservingContent(currentRegion)
    },

    refreshNavigationSearchPreservingContent() {
      let refreshSucceeded = false
      if (!this.searchAppliedKeyword || this.searchLoading) return
      this._searchRefreshAttempted = true
      const cycle = this.refreshCycle()
      const ticket = cycle.snapshot()
      const isCurrent = navigationResponseGuard(this)
      const restoreAnchor = this.captureContentAnchor()
      const beforeDevices = JSON.parse(JSON.stringify(this.searchDeviceList))
      const beforeScenes = JSON.parse(JSON.stringify(this.searchSceneList))
      const generation = ++this.searchRequestGeneration
      const requestedPageCount = Math.max(1, this.searchPageNo || 1)
      let sceneList = []
      let deviceList = []
      let regionOrder = []
      let completedPageNo = 0
      let hasMore = false
      this.searchLoading = true

      const requestPage = pageNo => {
        return lightRequest({
          method: 'post',
          url: '/home/findRegionNavigationSearchPage',
          data: {
            token: this.$store.state.token,
            keyword: this.searchAppliedKeyword,
            regionId: this.selectedNavigationRegionId,
            page: {
              pageNo: pageNo,
              pageSize: this.searchPageSize
            }
          }
        }).then(res => {
          if (generation !== this.searchRequestGeneration || !isCurrent() || !cycle.isCurrent(ticket))
            return Promise.reject({
              stale: true
            })
          if (!res.data || res.data.code != 0)
            throw res.data || {
              message: '搜索刷新失败'
            }
          const data = res.data.data || {}
          sceneList = this.mergeSearchScenes(sceneList, data.sceneList || [])
          deviceList = this.mergeSearchDevices(deviceList, data.deviceList || [])
          regionOrder = this.mergeSearchRegionOrder(regionOrder, data.regionIds || [])
          completedPageNo = pageNo
          hasMore = data.hasMore === true
          if (pageNo < requestedPageCount && hasMore) return requestPage(pageNo + 1)
        })
      }

      requestPage(1)
        .then(() => {
          if (generation !== this.searchRequestGeneration || !isCurrent() || !cycle.isCurrent(ticket)) return
          if (this.isInterfaceManagement) return
          this.searchSceneList = mergeRegionSnapshot(sceneList, beforeScenes, this.searchSceneList, row => String(row.sceneId))
          this.searchDeviceList = mergeRegionSnapshot(deviceList, beforeDevices, this.searchDeviceList,
            row => String(row.deviceId) + ':' + String(row.productId))
          this.searchRegionOrder = regionOrder
          this.searchPageNo = completedPageNo
          this.searchHasMore = hasMore
          this.searchPaginationLoadCompleted = completedPageNo > 1 && !hasMore
          restoreAnchor()
          refreshSucceeded = true
        })
        .catch(error => {
          if (generation !== this.searchRequestGeneration || (error && error.stale)) return
          // Preserve search cache silently; no retry within this display round.

        })
        .finally(() => {
          if (generation === this.searchRequestGeneration) {
            this.searchLoading = false
            this.scheduleCachedRegionRefresh()
            if (refreshSucceeded) {
              this.searchAutoFillBlocked = this.searchSceneList.length + this.searchDeviceList.length === 0
              this.scheduleViewportFill()
            }
          }
        })
    },

    loadNavigationSearch(reset) {
      if (!this.searchAppliedKeyword || this.searchLoading) return
      if (!reset && !this.searchHasMore) return
      const isCurrent = navigationResponseGuard(this)
      const refreshTicket = this.refreshCycle().snapshot()
      const generation = this.searchRequestGeneration
      let searchSucceeded = false
      const pageNo = reset ? 1 : this.searchPageNo + 1
      this.searchLoading = true
      lightRequest({
        method: 'post',
        url: '/home/findRegionNavigationSearchPage',
        data: {
          token: this.$store.state.token,
          keyword: this.searchAppliedKeyword,
          regionId: this.selectedNavigationRegionId,
          page: {
            pageNo: pageNo,
            pageSize: this.searchPageSize
          }
        }
      })
        .then(res => {
          if (generation !== this.searchRequestGeneration || !isCurrent()) return
          if (!res.data || res.data.code != 0)
            throw res.data || {
              message: '搜索失败'
            }
          const data = res.data.data || {}
          const scenes = data.sceneList || []
          const devices = data.deviceList || []
          const regionIds = data.regionIds || []
          const previousCount = reset ? 0 : this.searchSceneList.length + this.searchDeviceList.length
          this.searchSceneList = reset
            ? scenes
            : this.mergeSearchScenes(this.searchSceneList, scenes)
          this.searchDeviceList = reset
            ? devices
            : this.mergeSearchDevices(this.searchDeviceList, devices)
          this.searchRegionOrder = reset
            ? regionIds.slice()
            : this.mergeSearchRegionOrder(this.searchRegionOrder, regionIds)
          this.searchAutoFillBlocked = this.searchSceneList.length + this.searchDeviceList.length <= previousCount
          this.searchPageNo = pageNo
          this.searchHasMore = data.hasMore === true
          this.searchPaginationLoadCompleted = pageNo > 1 && !this.searchHasMore
          if (reset) this.markNavigationContentReady()
          if (this.refreshCycle().isCurrent(refreshTicket)) this._searchRefreshAttempted = true
          searchSucceeded = true
        })
        .catch(error => {
          if (generation !== this.searchRequestGeneration || !isCurrent()) return
          console.log('区域设备场景搜索失败', error)
          showTipsToast(
            this,
            3,
            error && error.message ? error.message : '搜索失败，请稍后重试',
            true,
            3000
          )
        })
        .finally(() => {
          if (generation === this.searchRequestGeneration) {
            this.searchLoading = false
            if (searchSucceeded) this.scheduleViewportFill()
            this.scheduleCachedRegionRefresh()
          }
        })
    },

    loadMoreNavigationSearch() {
      this.loadNavigationSearch(false)
    },

    mergeSearchScenes(current, incoming) {
      const result = (current || []).slice()
      const keys = {}
      result.forEach(scene => {
        keys[String((scene && scene.sceneId) || '')] = true
      })
      ;(incoming || []).forEach(scene => {
        const key = String((scene && scene.sceneId) || '')
        if (!key || keys[key]) return
        keys[key] = true
        result.push(scene)
      })
      return result
    },

    mergeSearchDevices(current, incoming) {
      const result = (current || []).slice()
      const keys = {}
      result.forEach(device => {
        keys[this.computedDeviceKey(device.deviceId, device.productId)] = true
      })
      ;(incoming || []).forEach(device => {
        const key = this.computedDeviceKey(device.deviceId, device.productId)
        if (keys[key]) return
        keys[key] = true
        result.push(device)
      })
      return result
    },

    mergeSearchRegionOrder(current, incoming) {
      const result = (current || []).slice()
      const keys = {}
      result.forEach(regionId => {
        keys[String(regionId || '')] = true
      })
      ;(incoming || []).forEach(regionId => {
        const key = String(regionId || '')
        if (!key || keys[key]) return
        keys[key] = true
        result.push(regionId)
      })
      return result
    },

    buildSearchNavigationSections() {
      const sectionsById = {}

      const ensureSection = item => {
        const regionId = String((item && item.regionId) || '')

        if (!sectionsById[regionId]) {
          sectionsById[regionId] = {
            regionId: regionId,
            regionName: item && item.regionName ? item.regionName : '未命名区域',
            sceneList: [],
            deviceList: [],
            itemCount: 0,
            truncated: false
          }
        }

        return sectionsById[regionId]
      }

      this.searchSceneList.forEach(scene => {
        ensureSection(scene).sceneList.push(scene)
      })
      this.searchDeviceList.forEach(device => {
        ensureSection(device).deviceList.push(device)
      })
      const order = {}
      this.searchRegionOrder.forEach((regionId, index) => {
        order[String(regionId || '')] = index
      })
      return Object.keys(sectionsById)
        .map(regionId => {
          const section = sectionsById[regionId]
          section.itemCount = section.sceneList.length + section.deviceList.length
          section.scenePlaceholderKeys = this.buildPlaceholderKeys(
            'search-scene-' + regionId,
            section.sceneList.length
          )
          section.devicePlaceholderKeys = this.buildPlaceholderKeys(
            'search-device-' + regionId,
            section.deviceList.length
          )
          return section
        })
        .sort((left, right) => {
          const leftOrder = Object.prototype.hasOwnProperty.call(order, left.regionId)
            ? order[left.regionId]
            : 2147483647
          const rightOrder = Object.prototype.hasOwnProperty.call(order, right.regionId)
            ? order[right.regionId]
            : 2147483647
          return leftOrder - rightOrder || left.regionName.localeCompare(right.regionName)
        })
    },

    navigationRegionChange(region) {
      if (!region || sameRegionId(region.regionId, this.selectedNavigationRegionId)) return
      this.headerAlignmentPendingAfterContent = false
      this.$store.dispatch('asyUpdataRegionShowPage', region)
      usersetStorage('selectRegionInfo', region)
      this.refreshQuickNavigationRegions(region.regionId)
      this.requestCategoryAlignment()

      if (this.searchResultActive) {
        this.reloadActiveNavigationSearch()
        return
      }

      this.resetNavigationContent(region.regionId)
      this.loadNavigationSelection('region-change', region)
    },

    navigationCategoryChange(category) {
      if (!category || category.code === this.selectedCategoryCode) return

      if (!jieMianStateJudge(this)) {
        this.$toast('排序管理时请查看所有对象~', 1200)
        return
      }

      this.headerAlignmentPendingAfterContent = false
      this.navigationAutomaticBatchCount = 0
      this.updateSelectedCategory(category.code)
      this.refreshDisplayNavigationSections(category.code)
      this.updateNavigationAllLoadedState()

      this.scheduleCachedRegionRefresh()
      this.scheduleViewportFill()
    },

    updateSelectedCategory(categoryCode) {
      if (categoryCode !== this.selectedCategoryCode) this.navigationPaginationLoadCompleted = false
      this.selectedCategoryCode = setRegionNavigationCategoryPreference(categoryCode)
    },

    requestHeaderAlignment() {
      this.$nextTick(() => {
        this.headerAlignmentSequence += 1
        const header = this.$refs.navigationHeader
        if (header) {
          header.align(header.$refs.regions)
          header.ensureCategoryVisible()
        }
      })
    },

    requestCategoryAlignment() {
      this.$nextTick(() => {
        const header = this.$refs.navigationHeader
        if (header && typeof header.ensureCategoryVisible === 'function')
          header.ensureCategoryVisible()
      })
    },

    reloadActiveNavigationSearch() {
      if (!this.searchResultActive) return
      this.searchRequestGeneration += 1
      this.searchSceneList = []
      this.searchDeviceList = []
      this.searchRegionOrder = []
      this.searchPageNo = 0
      this.searchHasMore = true
      this.searchLoading = false
      this.searchPaginationLoadCompleted = false
      this.navigationContentReady = false
      this.loadNavigationSearch(true)
    },

    navigationScrollToLower() {
      if (this.isInterfaceManagement) return

      if (this.searchResultActive) {
        this.loadMoreNavigationSearch()
        return
      }

      this.loadNextRegionBatch('scroll', this.selectedCategoryCode)
    },

    manualLoadMoreRegions() {
      if (this.isInterfaceManagement) return
      this.loadNextRegionBatch('manual', this.selectedCategoryCode)
    },

    resetNavigationContent(contentRegionId) {
      this.savedContentScrollTop = 0
      this.navigationRequestGeneration += 1
      this.loadedNavigationRegions = []
      this.navigationConfirmedCategoryAvailability = {}
      this.navigationConfirmedAvailableCategoryCodes = []
      this.navigationBatchLoading = false
      this.navigationSectionRefreshLoading = false
      this.navigationAllLoaded = false
      this.navigationSuccessfulBatchCount = 0
      this.navigationAutomaticBatchCount = 0
      this.navigationPaginationLoadCompleted = false
      this.navigationContentRegionId =
        contentRegionId == null ? this.selectedNavigationRegionId : contentRegionId
      this.ServeData = {
        deviceList: [],
        sceneList: []
      }
      this.displayNavigationSections = []
      this.navigationContentReady = false
      this.$store.dispatch('asyUpdataregionServeData', this.ServeData)
    },

    refreshNavigationSelectionPreservingContent(currentRegion) {
      const contentRegionChanged = !sameRegionId(
        this.navigationContentRegionId,
        currentRegion.regionId
      )
      this.navigationRequestGeneration += 1
      const generation = this.navigationRequestGeneration
      this.navigationContentRegionId = currentRegion.regionId
      this.navigationBatchLoading = false
      this.navigationSectionRefreshLoading = false

      if (contentRegionChanged) {
        this.commitNavigationContentReplacement([], [], [])
        this.displayNavigationSections = []
        this.navigationContentReady = false
      }

      const regions = this.isAllRegionNavigation
        ? this.getNavigationLoadCandidates().slice(0, REGION_CONTENT_BATCH_SIZE)
        : [currentRegion]

      if (regions.length === 0) {
        this.commitNavigationContentReplacement([], [], [])
        this.markNavigationContentReady()
        return
      }

      this.requestNavigationSections(
        regions,
        generation,
        this.isAllRegionNavigation,
        false,
        'activation',
        this.selectedCategoryCode,
        true
      )
    },

    commitNavigationContentReplacement(regions, deviceList, sceneList, publish) {
      this.loadedNavigationRegions = regions
      this.ServeData = {
        deviceList: deviceList,
        sceneList: sceneList
      }
      this.navigationAllLoaded = false
      this.navigationSuccessfulBatchCount = 0
      this.navigationAutomaticBatchCount = 0
      this.navigationPaginationLoadCompleted = false
      this.navigationConfirmedCategoryAvailability = {}
      this.navigationConfirmedAvailableCategoryCodes = []
      if (publish !== false) this.$store.dispatch('asyUpdataregionServeData', this.ServeData)
    },

    refreshLoadedNavigationSections(ticket) {
      const regions = this.loadedNavigationRegions.filter(region => ticket.ids.includes(String(region.regionId)))
      const cycle = this.refreshCycle()
      const isCurrent = navigationResponseGuard(this)
      const restoreAnchor = this.captureContentAnchor()
      const beforeDevices = JSON.parse(JSON.stringify(this.ServeData.deviceList))
      const beforeScenes = JSON.parse(JSON.stringify(this.ServeData.sceneList))
      let refreshSucceeded = false
      const generation = this.navigationRequestGeneration
      const targetIds = regions.map(region => String(region.regionId || '')).filter(Boolean)
      if (targetIds.length === 0) { cycle.finish(ticket); return }
      this.navigationSectionRefreshLoading = true
      lightRequest({
        method: 'post',
        url: '/home/findRegionSectionBatch',
        data: {
          token: this.$store.state.token,
          regionIds: targetIds
        }
      })
        .then(res => {
          if (generation !== this.navigationRequestGeneration || !isCurrent() || !cycle.isCurrent(ticket)) return
          if (!res.data || res.data.code != 0)
            throw res.data || {
              message: '刷新区域数据失败'
            }
          const responseSections = (res.data.data && res.data.data.sections) || []
          const responseMap = Object.create(null)
          responseSections.forEach(section => {
            if (section && section.regionId) responseMap[String(section.regionId)] = section
          })
          if (targetIds.some(id => !responseMap[id] || !Array.isArray(responseMap[id].deviceList) ||
              !Array.isArray(responseMap[id].sceneList))) throw new Error('Incomplete region snapshot')
          const oldRegionMap = Object.create(null)
          this.loadedNavigationRegions.forEach(region => {
            oldRegionMap[String(region.regionId || '')] = region
          })
          const normalizedSections = targetIds.map(regionId => {
            const oldRegion = oldRegionMap[regionId] || {}
            return (
              responseMap[regionId] || {
                regionId: regionId,
                regionName: oldRegion.regionName || '',
                categoryCodes: [],
                deviceList: [],
                sceneList: []
              }
            )
          })
          if (this.isInterfaceManagement) return
          this.applyConfirmedSectionCategories(normalizedSections)
          const targetMap = Object.create(null)
          targetIds.forEach(regionId => {
            targetMap[regionId] = true
          })
          const deviceList = this.ServeData.deviceList.filter(
            device => !targetMap[String(device.regionId || '')]
          )
          const sceneList = this.ServeData.sceneList.filter(
            scene => !targetMap[String(scene.regionId || '')]
          )
          normalizedSections.forEach(section => {
            deviceList.push.apply(deviceList, section.deviceList || [])
            sceneList.push.apply(sceneList, section.sceneList || [])
            const oldRegion = oldRegionMap[String(section.regionId)] || {}
            oldRegion.regionName = section.regionName || oldRegion.regionName
            oldRegion.truncated = section.truncated === true
          })
          this.sortLoadedNavigationRegions()
          this.ServeData = {
            deviceList: mergeRegionSnapshot(deviceList, beforeDevices, this.ServeData.deviceList,
              row => String(row.deviceId) + ':' + String(row.productId)),
            sceneList: mergeRegionSnapshot(sceneList, beforeScenes, this.ServeData.sceneList,
              row => String(row.sceneId))
          }
          this.$store.dispatch('asyUpdataregionServeData', this.ServeData)
          this.updateNavigationAllLoadedState()
          this.validateSelectedCategoryAvailability()
          this.refreshDisplayNavigationSections()
          restoreAnchor()
          refreshSucceeded = true
        })
        .catch(error => {
          if (generation !== this.navigationRequestGeneration || !isCurrent() || !cycle.isCurrent(ticket)) return
          // Silent failure: preserve cached cards; the next display round may retry.

        })
        .finally(() => {
          cycle.finish(ticket)
          if (generation !== this.navigationRequestGeneration) return
          this.navigationSectionRefreshLoading = false
          this.scheduleCachedRegionRefresh()
          if (refreshSucceeded) this.scheduleViewportFill()
        })
    },

    loadNavigationSelection(loadReason, selectedRegionSnapshot) {
      const reason = loadReason || 'initial'
      const selectedRegionId = selectedRegionSnapshot
        ? selectedRegionSnapshot.regionId
        : this.selectedNavigationRegionId

      if (normalizedRegionId(selectedRegionId) === '') {
        this.loadNextRegionBatch(reason, this.selectedCategoryCode)
        return
      }

      const region =
        selectedRegionSnapshot ||
        this.navigationRegionList.find(item => {
          return sameRegionId(item.regionId, selectedRegionId)
        })
      if (region)
        this.requestNavigationSections(
          [region],
          this.navigationRequestGeneration,
          false,
          false,
          reason
        )
    },

    loadNextRegionBatch(loadReason, requestCategoryCode) {
      if (
        !this.isAllRegionNavigation ||
        this.navigationBatchLoading ||
        this.navigationSectionRefreshLoading ||
        this.navigationAllLoaded
      )
        return
      const reason = loadReason || 'automatic'
      const categoryCode = requestCategoryCode || this.selectedCategoryCode
      const loadedIds = {}
      this.loadedNavigationRegions.forEach(region => {
        loadedIds[normalizedRegionId(region.regionId)] = true
      })
      const regions = this.getNavigationLoadCandidates().filter(region => {
        return !loadedIds[normalizedRegionId(region.regionId)]
      })
      const batch = regions.slice(0, REGION_CONTENT_BATCH_SIZE)

      if (batch.length === 0) {
        if (this.$store.state.regionnavigation.hasMore) {
          this.loadNextRegionIndexPage(reason, categoryCode)
          return
        }

        this.navigationAllLoaded = true
        this.markNavigationContentReady()
        return
      }

      this.requestNavigationSections(
        batch,
        this.navigationRequestGeneration,
        true,
        false,
        reason,
        categoryCode
      )
    },

    loadNextRegionIndexPage(loadReason, requestCategoryCode) {
      const generation = this.navigationRequestGeneration
      const previousIds = new Set(this.$store.state.regionnavigation.regionList.map(region => String(region.regionId)))
      requestNextRegionNavigationPage(this)
        .then(result => {
          if (result && (result.complete || result.skipped || result.stale)) return
          if (this._inactive || this._isDestroyed || generation !== this.navigationRequestGeneration) return
          const cache = this.$store.state.regionnavigation
          if (cache.hasMore && !cache.regionList.some(region => !previousIds.has(String(region.regionId)))) {
            showTipsToast(this, 3, '加载更多区域失败，请稍后重试', true, 2500)
            return
          }
          this.enableNavigationModeFromCache({ alignHeader: false })
          this.$nextTick(() => {
            if (!this._inactive && !this._isDestroyed && generation === this.navigationRequestGeneration)
              this.loadNextRegionBatch(loadReason, requestCategoryCode)
          })
        })
        .catch(error => {
          console.log('获取下一页区域索引失败', error)
          showTipsToast(
            this,
            3,
            error && error.message ? error.message : '加载更多区域失败，请稍后重试',
            true,
            2500
          )
        })
    },

    getNavigationLoadCandidates() {
      return this.navigationRegionList.slice(1)
    },

    updateNavigationAllLoadedState() {
      if (!this.isAllRegionNavigation) return
      this.navigationAllLoaded =
        !this.$store.state.regionnavigation.hasMore &&
        this.getNavigationLoadCandidates().every(region => {
          return this.loadedNavigationRegions.some(loaded =>
            sameRegionId(loaded.regionId, region.regionId)
          )
        })
      this.validateSelectedCategoryAvailability()
    },

    requestNavigationSections(
      regions,
      generation,
      isAllRegionBatch,
      replaceExisting,
      loadReason,
      requestCategoryCode,
      replaceEntireContent
    ) {
      if (!regions || regions.length === 0) return
      const shouldAlignHeaderAfterResponse =
        this.headerAlignmentPendingAfterContent && this.loadedNavigationRegions.length === 0
      const isCurrent = navigationResponseGuard(this)
      const refreshTicket = this.refreshCycle().snapshot()
      this.navigationBatchLoading = true
      lightRequest({
        method: 'post',
        url: '/home/findRegionSectionBatch',
        data: {
          token: this.$store.state.token,
          regionIds: regions.map(region => region.regionId)
        }
      })
        .then(res => {
          if (generation !== this.navigationRequestGeneration) return
          if (!isCurrent()) { this.navigationBatchLoading = false; return }

          if (res.data.code != 0) {
            this.navigationBatchLoading = false
            showTipsToast(this, 3, res.data.message, true, 3000)
            this.scheduleCachedRegionRefresh()
            return
          }

          const responseSections = (res.data.data && res.data.data.sections) || []
          const responseSectionMap = Object.create(null)
          responseSections.forEach(section => {
            if (section && section.regionId != null)
              responseSectionMap[String(section.regionId)] = section
          })
          const sections = regions.map(region => {
            const regionId = String(region && region.regionId != null ? region.regionId : '')
            const responseSection = responseSectionMap[regionId]

            if (responseSection) {
              return Object.assign({}, responseSection, {
                regionId: region.regionId
              })
            }

            return {
              regionId: region.regionId,
              regionName: region && region.regionName ? region.regionName : '',
              categoryCodes: [],
              deviceList: [],
              sceneList: []
            }
          })

          if (replaceEntireContent === true) {
            this.commitNavigationContentReplacement([], [], [], false)
          }

          this.applyConfirmedSectionCategories(sections)
          const returnedRegionIds = sections.map(section => normalizedRegionId(section.regionId))
          const returnedRegionIdMap = Object.create(null)
          returnedRegionIds.forEach(regionId => {
            returnedRegionIdMap[regionId] = true
          })
          const shouldReplace =
            replaceEntireContent === true ||
            replaceExisting === true ||
            returnedRegionIds.some(regionId => {
              return this.loadedNavigationRegions.some(region =>
                sameRegionId(region.regionId, regionId)
              )
            })
          const deviceList = shouldReplace
            ? this.ServeData.deviceList.filter(device => {
                return !returnedRegionIdMap[normalizedRegionId(device.regionId)]
              })
            : this.ServeData.deviceList.slice()
          const sceneList = shouldReplace
            ? this.ServeData.sceneList.filter(scene => {
                return !returnedRegionIdMap[normalizedRegionId(scene.regionId)]
              })
            : this.ServeData.sceneList.slice()

          if (shouldReplace) {
            this.loadedNavigationRegions = this.loadedNavigationRegions.filter(region => {
              return !returnedRegionIdMap[normalizedRegionId(region.regionId)]
            })
          }

          sections.forEach(section => {
            this.loadedNavigationRegions.push({
              regionId: section.regionId,
              regionName: section.regionName,
              truncated: section.truncated === true
            })
            deviceList.push.apply(deviceList, section.deviceList || [])
            sceneList.push.apply(sceneList, section.sceneList || [])
          })
          this.sortLoadedNavigationRegions()
          this.ServeData = {
            deviceList: deviceList,
            sceneList: sceneList
          }

          if (isAllRegionBatch) {
            this.navigationSuccessfulBatchCount += 1

            if (
              this.navigationSuccessfulBatchCount > 1 &&
              requestCategoryCode === this.selectedCategoryCode
            ) {
              this.navigationPaginationLoadCompleted = true
            }
          }

          this.$store.dispatch('asyUpdataregionServeData', this.ServeData)

          if (isAllRegionBatch) {
            this.updateNavigationAllLoadedState()
          } else {
            this.navigationAllLoaded = true
            this.prepareInterfaceManagementState()
          }

          this.validateSelectedCategoryAvailability()
          this.refreshDisplayNavigationSections()
          this.markNavigationContentReady()
          if (loadReason === 'region-change') this.requestCategoryAlignment()

          this.refreshCycle().markFresh(returnedRegionIds, refreshTicket)

          this.navigationBatchLoading = false

          if (shouldAlignHeaderAfterResponse && this.headerAlignmentPendingAfterContent) {
            this.headerAlignmentPendingAfterContent = false
            this.requestHeaderAlignment()
          }

          this.scheduleCachedRegionRefresh()

          if (isAllRegionBatch) this.scheduleViewportFill()
        })
        .catch(error => {
          if (generation === this.navigationRequestGeneration) {
            this.navigationBatchLoading = false
            console.log('区域分批加载失败', error)
            showTipsToast(
              this,
              3,
              error && error.message ? error.message : '加载更多区域失败，请稍后重试',
              true,
              2500
            )
            this.scheduleCachedRegionRefresh()
          }
        })
    },

    applyConfirmedSectionCategories(sections) {
      ;(sections || []).forEach(section => {
        if (!section || !section.regionId) return
        let codes = Array.isArray(section.categoryCodes) ? section.categoryCodes.slice() : []

        if (!Array.isArray(section.categoryCodes)) {
          if ((section.sceneList || []).length > 0) codes.push('scene')
          ;(section.deviceList || []).forEach(device => {
            const code = device && device.categoryCode ? device.categoryCode : 'other'
            if (codes.indexOf(code) < 0) codes.push(code)
          })
        }

        const codeMap = {}
        codes.forEach(code => {
          codeMap[code] = true
        })
        const orderedCodes = this.navigationCategoryList
          .filter(category => category.code !== 'all' && codeMap[category.code])
          .map(category => category.code)
        this.$set(this.navigationConfirmedCategoryAvailability, section.regionId, orderedCodes)
      })
      const allCodeMap = {}
      Object.keys(this.navigationConfirmedCategoryAvailability).forEach(regionId => {
        ;(this.navigationConfirmedCategoryAvailability[regionId] || []).forEach(code => {
          allCodeMap[code] = true
        })
      })
      this.navigationConfirmedAvailableCategoryCodes = this.navigationCategoryList
        .filter(category => category.code !== 'all' && allCodeMap[category.code])
        .map(category => category.code)
      const visibleCodes = this.isAllRegionNavigation
        ? this.navigationConfirmedAvailableCategoryCodes
        : this.navigationConfirmedCategoryAvailability[this.selectedNavigationRegionId] || []
      this.validateSelectedCategoryAvailability(visibleCodes)
    },

    validateSelectedCategoryAvailability(visibleCodes) {
      if (this.selectedCategoryCode === 'all') return
      const availableCodes =
        visibleCodes ||
        (this.isAllRegionNavigation
          ? this.navigationConfirmedAvailableCategoryCodes
          : this.navigationConfirmedCategoryAvailability[this.selectedNavigationRegionId] || [])
      if (availableCodes.indexOf(this.selectedCategoryCode) >= 0) return
      if (this.isAllRegionNavigation && !this.navigationAllLoaded) return
      this.updateSelectedCategory('all')
      this.requestCategoryAlignment()
    },

    buildNavigationSections(categoryCode) {
      const code = categoryCode || 'all'
      const showScenes = code === 'all' || code === 'scene'
      const showDevices = code !== 'scene'
      const sections = this.loadedNavigationRegions.map(region => {
        const sceneList = showScenes
          ? this.ServeData.sceneList.filter(scene => sameRegionId(scene.regionId, region.regionId))
          : []
        const deviceList = showDevices
          ? this.ServeData.deviceList.filter(device => {
              if (!sameRegionId(device.regionId, region.regionId)) return false
              return code === 'all' || device.categoryCode === code
            })
          : []
        const itemCount = sceneList.length + deviceList.length
        return {
          regionId: region.regionId,
          regionName: region.regionName,
          truncated: region.truncated,
          sceneList: sceneList,
          deviceList: deviceList,
          itemCount: itemCount,
          scenePlaceholderKeys: this.buildPlaceholderKeys(
            'navigation-scene-' + region.regionId,
            sceneList.length
          ),
          devicePlaceholderKeys: this.buildPlaceholderKeys(
            'navigation-device-' + region.regionId,
            deviceList.length
          )
        }
      })

      if (this.isAllRegionNavigation && code !== 'all') {
        return sections.filter(section => section.itemCount > 0)
      }

      return sections
    },

    buildPlaceholderKeys(prefix, itemCount) {
      if (itemCount <= 0) return []
      const columnCount = this.computedControlLayout.columnCount
      const placeholderCount = (columnCount - (itemCount % columnCount)) % columnCount
      return new Array(placeholderCount)
        .fill(0)
        .map((value, index) => prefix + '-placeholder-' + index)
    },

    refreshDisplayNavigationSections(categoryCode) {
      this.displayNavigationSections = this.buildNavigationSections(
        categoryCode || this.selectedCategoryCode
      )
    },

    buildInterfaceManagementOrder(list) {
      const selected = list
        .filter(item => Number(item.followState || 0) === 1)
        .slice()
        .sort((left, right) => Number(left.followOrder || 0) - Number(right.followOrder || 0))
      return selected.concat(list.filter(item => selected.indexOf(item) < 0))
    },

    resizeContent() {
      this.controlViewportWidth = this.$el ? this.$el.clientWidth : getControlViewportWidth()
      this.scheduleViewportFill()
    },

    computedDeviceKey(deviceId, productId) {
      return String(deviceId) + ':' + String(productId)
    },

    prepareInterfaceManagementState() {
      if (this.isAllRegionNavigation && this.isInterfaceManagement)
        this.$store.dispatch('asyUpdataobjectMoveState', false)
    },

    openRegionManage() {
      this.$refs.navigationHeader.openMore()
    },

    sortDataHandle() {},

    scheduleViewportFill() {
      this.$nextTick(() => {
        if (this._inactive || this._isDestroyed || this.isInterfaceManagement) return
        const el = this.$refs.content
        if (!el || el.clientHeight <= 0) return
        if (this.searchResultActive) {
          if (!this.searchLoading && !this.searchAutoFillBlocked && this.searchHasMore && el.scrollHeight <= el.clientHeight + 1)
            this.loadMoreNavigationSearch()
        } else if (this.shouldAutomaticallyLoadMoreRegions()) {
          this.navigationAutomaticBatchCount += 1
          this.loadNextRegionBatch('automatic', this.selectedCategoryCode)
        }
      })
    },

    shouldAutomaticallyLoadMoreRegions() {
      const el = this.$refs.content
      return !!(
        this.navigationInitialized && this.isAllRegionNavigation && !this.searchResultActive &&
        !this.navigationAllLoaded && !this.navigationBatchLoading &&
        !this.navigationSectionRefreshLoading && el && el.clientHeight > 0 &&
        (el.scrollHeight <= el.clientHeight + 1 ||
          (this.navigationAutomaticBatchCount < MAX_AUTOMATIC_REGION_BATCHES &&
           el.scrollHeight <= el.clientHeight + 500))
      )
    },

    onScroll() {
      if (this._inactive || this.restoringContentScroll) return
      if (this.$refs.content) this.savedContentScrollTop = this.$refs.content.scrollTop
      this.scheduleCachedRegionRefresh()
      const el = this.$refs.content
      if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 500)
        this.navigationScrollToLower()
    },

    syncSortedLists() {
      this.$store.dispatch('asyUpdataregionServeData', this.ServeData)
      this.refreshDisplayNavigationSections()
    },

    activatePage(displayRound = false) {
      if (!canUseModernNavigation(this)) return
      console.log('页面显示')
      createSocket(this)
      ensureRegionNavigationScope(this)
      const selectionChangedOutsideHeader = !sameRegionId(
        this.navigationContentRegionId,
        this.selectedNavigationRegionId
      )

      if (!this.searchMode) {
        const cache = this.$store.state.regionnavigation

        const navigationReady = cache.initialized &&
          ((cache.navigation || {}).categories || []).length > 0
        if (!navigationReady) {
          this.headerAlignmentPendingAfterContent = true
          this.loadNavigationPage()
          return
        }
        if (!this.navigationInitialized) {
          this.headerAlignmentPendingAfterContent = true
          this.enableNavigationModeFromCache()
        } else if (
          this.navigationCacheGeneration !== cache.generation ||
          selectionChangedOutsideHeader
        ) {
          this.headerAlignmentPendingAfterContent = true
          this.enableNavigationModeFromCache()
        } else if (!this.navigationContentReady && !this.navigationBatchLoading) {
          // Navigation may be cached even though the first content read failed/was interrupted.
          // This is a normal load, not a silent refresh of nonexistent cards.
          this.navigationAllLoaded = false
          this.loadNavigationSelection('initial')
        } else {
          this.requestHeaderAlignment()
        }

        this.scheduleCachedRegionRefresh()
      } else if (selectionChangedOutsideHeader) {
        this.headerAlignmentPendingAfterContent = true
        this.enableNavigationModeFromCache()
        this.reloadActiveNavigationSearch()
      } else {
        this.scheduleCachedRegionRefresh()
      }

      if (displayRound && this.$store.state.regionnavigation.initialized) this.refreshNavigationIndexOnActivation()
    }
  },
  watch: {
    isInterfaceManagement(value) { if (!value) this.scheduleCachedRegionRefresh() },
    '$store.state.regionnavigation.refreshSequence'() {
      if (!this._inactive) {
        // Explicit refresh after editing uses the same scheduler, never a parallel invalid-ID queue.
        this.hideCachedRegions()
        this.showCachedRegions()
      }
    },
    '$store.state.publicinfo.regionServeData': {
      handler(newValue, oldValue) {
        console.log('设备展示数据发生了改变1')
        this.ServeData = newValue
        this.refreshDisplayNavigationSections()
      },

      deep: true
    },

    '$store.state.publicinfo.regionShowPage.id'() {
      if (!this._inactive) this.activatePage()
    }
  },
  computed: {
    selectedNavigationRegionId() {
      return this.$store.state.publicinfo.regionShowPage.id || ''
    },

    isAllRegionNavigation() {
      return this.selectedNavigationRegionId === ''
    },

    isInterfaceManagement() {
      return this.$store.state.configinfo.objectMoveState === true
    },

    searchResultActive() {
      return this.searchMode && !!this.searchAppliedKeyword
    },

    renderNavigationSections() {
      return this.searchResultActive
        ? this.buildSearchNavigationSections()
        : this.displayNavigationSections
    },

    visibleNavigationCategoryList() {
      const availableCodes = {}

      if (this.isAllRegionNavigation) {
        this.navigationConfirmedAvailableCategoryCodes.forEach(code => {
          availableCodes[code] = true
        })
      } else {
        ;(
          this.navigationConfirmedCategoryAvailability[this.selectedNavigationRegionId] || []
        ).forEach(code => {
          availableCodes[code] = true
        })
      }

      const currentRegionResolved = this.loadedNavigationRegions.some(region => {
        return sameRegionId(region.regionId, this.selectedNavigationRegionId)
      })
      const keepRestoredSelection = this.isAllRegionNavigation
        ? !this.navigationAllLoaded
        : !currentRegionResolved
      return this.navigationCategoryList.filter(category => {
        return (
          category.code === 'all' ||
          availableCodes[category.code] ||
          (keepRestoredSelection && category.code === this.selectedCategoryCode)
        )
      })
    },

    computedNavigationContentHeightPx() {
      return Math.max(0, this.computedcontrolHeightPx - (this.isInterfaceManagement ? 0 : 44))
    },

    computedEmptyPageText() {
      return '当前区域暂无设备或场景'
    },

    computedSortScenePlaceholderKeys() {
      return this.buildPlaceholderKeys('sort-scene', this.ServeData.sceneList.length)
    },

    computedSortDevicePlaceholderKeys() {
      return this.buildPlaceholderKeys('sort-device', this.ServeData.deviceList.length)
    },

    computedControlLayout() {
      return getControlLayoutMetrics(this.controlViewportWidth)
    },

    computedcontrolHeightPx() {
      return Math.max(240, window.innerHeight - 170)
    }
  },
  name: 'ModernRegion',
  components: {
    DeviceItemView,
    SceneItemView,
    RegionHeader,
    draggable
  },

  mounted() {
    this.resizeContent()
    window.addEventListener('resize', this.resizeContent)
    document.addEventListener('visibilitychange', this.onDocumentVisibility)
    window.addEventListener('pagehide', this.hideCachedRegions)
    window.addEventListener('pageshow', this.onDocumentVisibility)
    window.addEventListener('controlDeviceState', this.syncSearchDeviceState)
  },

  deactivated() {
    this.hideCachedRegions()
    this.restoringContentScroll = true
  },
  activated() {
    this.restoringContentScroll = true
    this.showCachedRegions()
    this.$nextTick(() => {
      if (this._inactive) return
      if (this.$refs.content) this.$refs.content.scrollTop = this.savedContentScrollTop
      this.restoringContentScroll = false
      this.scheduleCachedRegionRefresh()
      this.scheduleViewportFill()
    })
  },

  beforeDestroy() {
    this.hideCachedRegions()
    document.removeEventListener('visibilitychange', this.onDocumentVisibility)
    window.removeEventListener('pagehide', this.hideCachedRegions)
    window.removeEventListener('pageshow', this.onDocumentVisibility)
    window.removeEventListener('resize', this.resizeContent)
    window.removeEventListener('controlDeviceState', this.syncSearchDeviceState)
    this.navigationRequestGeneration++
    this.searchRequestGeneration++
  }
}
</script>
<style scoped>
.modern-region {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.region-content {
  overflow: auto;
  flex: 1;
  min-height: 0;
  padding: 0 0 100px;
}
.region-section {
  margin-bottom: 8px;
}
.region-section > .control-grid { margin-bottom: 0; }
.region-section > .scene-grid + .control-grid { margin-top: 10px; }
.region-section h3 {
  height: 48px;
  line-height: 48px;
  font-weight: 700;
  font-size: 18px;
  margin: 0 4px;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
